import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import * as yup from 'yup'
import { useForm, FormProvider, useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import axios from '../../dataClient/dataClient'
import { FlexColumn, Form, LightBGContainer, FlexEndContainer, ColumnWithScrollbar } from '../styles/Containers'
import { ErrorMessage } from '../common/UserMessage'
import { MedBodyText } from '../styles/Text'
import Button from '../common/Button'
import DuplicateFile from './DuplicateFile'
import { theme } from '../../themes/theme'

const StyledForm = styled(Form)`
  flex: 1;
  min-height: 0;
`

const ScrollableContent = styled(ColumnWithScrollbar)`
  min-height: 0;
`

const ListContainer = styled(LightBGContainer)`
  border: 1px solid ${theme.colors.sectionDivider};
`

const StickyFooter = styled(FlexColumn)`
  flex-shrink: 0;
`

const schema = yup.object().shape({
  duplicates: yup
    .array()
    .of(
      yup.object().shape({
        shouldKeep: yup.boolean().required('Value is required')
      })
    )
    .required('Duplicate values are required')
})

function DuplicateGroup({
  categoryId,
  files,
  isReviewed = false,
  activeFiles = [],
  hasNextGroup = true,
  onUpdateActiveFiles = () => {},
  onSuccess = () => {}
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const [updateFile1, setUpdateFile1] = useState(true)

  const methods = useForm({ resolver: yupResolver(schema) })
  const { handleSubmit, control, reset } = methods
  const { append } = useFieldArray({ control, name: 'duplicates' })

  useEffect(() => {
    if (files) {
      reset()
      if (isReviewed) {
        for (const file of files) append({ fileId: file.id, removed: file.removed, shouldKeep: !file.removed })
      } else {
        for (const file of files) append({ fileId: file.id, removed: false, shouldKeep: true })
      }
    }
  }, [files])

  function updateActiveFiles(fileId) {
    if (!activeFiles.includes(fileId)) {
      onUpdateActiveFiles(fileId, updateFile1)
      setUpdateFile1(prevValue => !prevValue)
    }
  }

  const onError = (errors, e) => console.error('Error', errors, e)

  async function onSubmit(data) {
    setIsLoading(true)
    setError(false)

    const filesToRemove = data.duplicates.filter(d => !d.shouldKeep && !d.removed)
    const filesToAdd = data.duplicates.filter(d => d.shouldKeep && d.removed)
    const failed = []

    for (const file of filesToRemove) {
      await axios.post(`/file/${file.fileId}/archive`, { reason: 'Duplicate' }).catch(e => {
        console.error('Failed archiving file', file.fileId, e)
        failed.push(file)
      })
    }

    for (const file of filesToAdd) {
      await axios.post(`/category/${categoryId}/restoreFile`, { fileId: file.fileId }).catch(e => {
        console.error('Failed restoring file', file.fileId, e)
        failed.push(file)
      })
    }

    if (failed.length > 0) {
      setError(true)
    } else {
      onSuccess(data.duplicates)
    }

    setIsLoading(false)
  }

  return (
    <FormProvider {...methods}>
      <StyledForm onSubmit={handleSubmit(onSubmit, onError)}>
        <ScrollableContent>
          <ListContainer padding="15px">
            <FlexColumn gap="10px">
              <MedBodyText>
                The following files are possible duplicates. Select the file(s) you would like to keep and which you would like to remove. You must
                save to preserve your selection.
              </MedBodyText>
              <FlexColumn gap="5px">
                {files.map((f, index) => {
                  return (
                    <DuplicateFile
                      key={index}
                      id={`duplicates.${index}`}
                      fileId={f.id}
                      filename={f.filename}
                      active={activeFiles.includes(f.id)}
                      onClickFile={() => updateActiveFiles(f.id)}
                      disabled={isLoading}
                    />
                  )
                })}
              </FlexColumn>
            </FlexColumn>
          </ListContainer>
        </ScrollableContent>
        <StickyFooter gap="20px">
          {error && <ErrorMessage>There was an error saving your selection. Please try again.</ErrorMessage>}
          <FlexEndContainer>
            <Button type="submit" loading={isLoading}>
              Save{hasNextGroup && ' & Review Next Group'}
            </Button>
          </FlexEndContainer>
        </StickyFooter>
      </StyledForm>
    </FormProvider>
  )
}

DuplicateGroup.propTypes = {
  categoryId: PropTypes.string.isRequired,
  files: PropTypes.array.isRequired,
  isReviewed: PropTypes.bool,
  activeFiles: PropTypes.array,
  hasNextGroup: PropTypes.bool,
  onSuccess: PropTypes.func
}

export default DuplicateGroup
